import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import knowledgeApi from '@/lib/knowledge-api'

export interface KnowledgeBase {
  id: string
  name: string
  description: string
  visibility: 'private' | 'public'
  document_count: number
  ready_document_count: number
  created_at: string
  updated_at: string
}

export interface PaginatedResponse<T> {
  items: T[]
  total: number
  page: number
  page_size: number
}

export interface PaginationParams {
  page: number
  pageSize: number
  search: string
}

export function useKnowledgeBases(params?: PaginationParams) {
  const { page = 1, pageSize = 10, search = '' } = params || {}
  return useQuery({
    queryKey: ['knowledge-bases', page, pageSize, search],
    queryFn: async () => {
      const response = await knowledgeApi.get('/knowledge/api/kb/list/', {
        params: { page, page_size: pageSize, search },
      })
      return response.data as PaginatedResponse<KnowledgeBase>
    },
  })
}

export function useBulkDeleteKnowledgeBases() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (ids: string[]) => {
      const response = await knowledgeApi.post('/knowledge/api/kb/bulk-delete/', { ids })
      return response.data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['knowledge-bases'] })
    },
  })
}

export function useCreateKnowledgeBase() {
  const queryClient = useQueryClient()
  
  return useMutation({
    mutationFn: async (data: { name: string; description?: string; chunk_method?: string }) => {
      const response = await knowledgeApi.post('/knowledge/api/kb/create/', data)
      return response.data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['knowledge-bases'] })
    },
  })
}

export function useDeleteKnowledgeBase() {
  const queryClient = useQueryClient()
  
  return useMutation({
    mutationFn: async (kbId: string) => {
      await knowledgeApi.delete(`/knowledge/api/kb/${kbId}/delete/`)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['knowledge-bases'] })
    },
  })
}

// ── Import from RAGflow ──────────────────────────────────────────────

export interface ImportableDataset {
  id: string
  name: string
  description: string
  chunk_method: string
  embedding_model: string
  document_count: number
  linked_tagset_count: number
  created_at: string
}

export function useImportableDatasets(params?: PaginationParams) {
  const { page = 1, pageSize = 50, search = '' } = params || {}
  return useQuery({
    queryKey: ['importable-datasets', page, pageSize, search],
    queryFn: async () => {
      const response = await knowledgeApi.get('/knowledge/api/kb/import/available/', {
        params: { page, page_size: pageSize, search },
      })
      return response.data as PaginatedResponse<ImportableDataset>
    },
  })
}

export function useImportKnowledgeBase() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (data: { ragflow_dataset_id: string; name?: string }) => {
      const response = await knowledgeApi.post('/knowledge/api/kb/import/', data)
      return response.data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['knowledge-bases'] })
      queryClient.invalidateQueries({ queryKey: ['importable-datasets'] })
    },
  })
}
